<template>
  <Teleport to="body">
    <Transition name="wizard-fade">
      <div v-if="show" class="wizard-overlay" role="dialog" aria-modal="true" aria-label="Casino Setup Wizard">
        <div class="wizard-panel">

          <!-- ─── Header ─────────────────────────────────────────────────── -->
          <div class="wizard-header">
            <div class="wizard-brand">
              <svg class="wizard-brand-icon" viewBox="0 0 32 32" fill="none">
                <rect width="32" height="32" rx="8" fill="var(--accent-primary, #00e701)" fill-opacity="0.15"/>
                <path d="M16 6l2.5 7.5H26l-6.5 4.5 2.5 7.5L16 21l-6 4.5 2.5-7.5L6 13.5h7.5L16 6z" fill="var(--accent-primary, #00e701)"/>
              </svg>
              <span class="wizard-brand-text">Casino Setup Wizard</span>
            </div>
            <p class="wizard-subtitle">Configure your casino in {{ totalSteps }} easy steps. You can always change these settings later in the admin panel.</p>
          </div>

          <!-- ─── Step Indicator ─────────────────────────────────────────── -->
          <div class="wizard-steps-bar">
            <div
              v-for="(step, i) in steps"
              :key="step.step"
              class="step-item"
              :class="{
                'step-done': step.completed,
                'step-active': step.step === currentStep && !step.completed,
                'step-upcoming': step.step > currentStep && !step.completed,
              }"
            >
              <div class="step-circle">
                <svg v-if="step.completed" class="step-check" viewBox="0 0 16 16" fill="none">
                  <path d="M3.5 8l3 3 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-else>{{ step.step }}</span>
              </div>
              <span class="step-label">{{ step.title }}</span>
              <div v-if="i < steps.length - 1" class="step-connector" :class="{ 'connector-done': step.completed }" />
            </div>
          </div>

          <!-- ─── Loading ────────────────────────────────────────────────── -->
          <div v-if="loading" class="wizard-body wizard-loading">
            <div class="loading-spinner" />
            <span>Loading setup status…</span>
          </div>

          <!-- ─── Step Content ───────────────────────────────────────────── -->
          <div v-else class="wizard-body">
            <div class="step-header">
              <h2 class="step-title">{{ activeStep?.title }}</h2>
              <p class="step-desc">{{ activeStep?.description }}</p>
            </div>

            <div v-if="error" class="wizard-error">{{ error }}</div>

            <!-- Step 1: Casino Name & Domain -->
            <div v-if="currentStep === 1" class="step-content">
              <div class="field-group">
                <label class="field-label">Casino Name <span class="required">*</span></label>
                <input
                  v-model="step1.siteName"
                  type="text"
                  class="field-input"
                  placeholder="e.g. Lucky Star Casino"
                  maxlength="100"
                />
                <p class="field-hint">Displayed in the browser title, email subjects, and throughout the site.</p>
              </div>

              <div class="field-group">
                <label class="field-label">Application URL</label>
                <input
                  v-model="step1.appUrl"
                  type="url"
                  class="field-input"
                  placeholder="https://casino.example.com"
                  maxlength="255"
                />
                <p class="field-hint">The full URL where your casino is hosted. Used for links in emails and the PWA manifest.</p>
              </div>

              <div class="field-group">
                <label class="field-label">Timezone</label>
                <select v-model="step1.timezone" class="field-select">
                  <option v-for="tz in commonTimezones" :key="tz.value" :value="tz.value">{{ tz.label }}</option>
                </select>
                <p class="field-hint">Used for reporting, scheduled jobs, and displaying dates in the admin panel.</p>
              </div>
            </div>

            <!-- Step 2: Branding -->
            <div v-if="currentStep === 2" class="step-content">
              <!-- Presets -->
              <div class="field-group">
                <label class="field-label">Theme Preset</label>
                <p class="field-hint">Choose a starting point for your brand. You can fine-tune every detail in Settings → Theme.</p>
                <div v-if="presetsLoading" class="loading-inline">Loading presets…</div>
                <div v-else class="presets-grid">
                  <button
                    v-for="preset in presets"
                    :key="preset.id"
                    type="button"
                    class="preset-card"
                    :class="{ 'preset-active': step2.presetName === preset.name }"
                    @click="selectPreset(preset)"
                  >
                    <div class="preset-swatches">
                      <span
                        v-for="(color, ci) in preset.thumbnail_colors.slice(0, 4)"
                        :key="ci"
                        class="preset-swatch"
                        :style="{ background: color }"
                      />
                    </div>
                    <span class="preset-name">{{ preset.name }}</span>
                    <span v-if="preset.description" class="preset-desc">{{ preset.description }}</span>
                  </button>
                </div>
              </div>

              <!-- Primary color -->
              <div class="field-group">
                <label class="field-label">Primary Accent Color</label>
                <div class="color-row">
                  <input type="color" v-model="step2.primaryColor" class="color-swatch-input" />
                  <input type="text" v-model="step2.primaryColor" class="field-input color-hex-input" maxlength="7" placeholder="#00e701" />
                </div>
                <p class="field-hint">Main action color — buttons, links, highlights. Can be changed anytime in Settings → Theme.</p>
              </div>

              <!-- Logo upload -->
              <div class="field-group">
                <label class="field-label">Logo (optional)</label>
                <div class="logo-upload-area" @click="logoInputEl?.click()">
                  <img v-if="logoPreviewUrl" :src="logoPreviewUrl" alt="Logo preview" class="logo-preview" />
                  <div v-else class="logo-placeholder">
                    <svg viewBox="0 0 24 24" fill="none" class="upload-icon">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                      <polyline points="17 8 12 3 7 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    <span>Click to upload PNG / JPG / SVG</span>
                    <span class="upload-hint">Recommended: 200 × 60 px</span>
                  </div>
                  <input
                    ref="logoInputEl"
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg"
                    class="hidden-file-input"
                    @change="onLogoChange"
                  />
                </div>
                <p v-if="logoUploadError" class="field-error">{{ logoUploadError }}</p>
                <p class="field-hint">You can also upload the logo later in Settings → Theme. Recommended size: 200 × 60 px.</p>
              </div>
            </div>

            <!-- Step 3: Currency -->
            <div v-if="currentStep === 3" class="step-content">
              <div class="field-group">
                <label class="field-label">Default Currency <span class="required">*</span></label>
                <select v-model="step3.defaultCurrency" class="field-select">
                  <option v-for="c in commonCurrencies" :key="c.code" :value="c.code">{{ c.code }} — {{ c.name }}</option>
                </select>
                <p class="field-hint">The primary currency displayed on the site. Players deposit and wager in this currency.</p>
              </div>

              <div class="field-group">
                <label class="field-label">Decimal Places</label>
                <select v-model="step3.currencyDecimalPlaces" class="field-select">
                  <option :value="0">0 — whole numbers only (e.g. 100)</option>
                  <option :value="2">2 — standard (e.g. 100.00)</option>
                  <option :value="8">8 — crypto precision (e.g. 0.00100000)</option>
                </select>
              </div>

              <div class="currency-preview">
                <span class="preview-label">Balance preview</span>
                <span class="preview-value">{{ step3.defaultCurrency }} {{ currencyPreviewValue }}</span>
              </div>
            </div>

            <!-- Step 4: Payment Methods -->
            <div v-if="currentStep === 4" class="step-content">
              <div v-if="step4Loading" class="loading-inline">Loading payment methods…</div>
              <div v-else-if="step4Methods.length === 0" class="empty-notice">
                No payment methods found. You can add them in Settings → Payments after setup.
              </div>
              <div v-else class="methods-list">
                <div v-for="method in step4Methods" :key="method.id" class="method-row">
                  <div class="method-info">
                    <span class="method-name">{{ method.display_name }}</span>
                    <span class="method-meta">{{ method.provider }} · {{ method.type }}</span>
                  </div>
                  <label class="toggle-switch" :aria-label="`Enable ${method.display_name}`">
                    <input
                      type="checkbox"
                      :checked="method.enabled"
                      @change="method.enabled = ($event.target as HTMLInputElement).checked"
                    />
                    <span class="toggle-track">
                      <span class="toggle-knob" />
                    </span>
                  </label>
                </div>
              </div>
              <p class="field-hint">Enable the payment methods you want to offer players. You can configure API keys and limits in Settings → Payments.</p>
            </div>

            <!-- Step 5: Game Selection -->
            <div v-if="currentStep === 5" class="step-content">
              <div class="games-toolbar">
                <button type="button" class="btn-text" @click="selectAllGames(true)">Enable All</button>
                <button type="button" class="btn-text" @click="selectAllGames(false)">Disable All</button>
              </div>

              <div class="games-grid">
                <div v-for="game in step5Games" :key="game.slug" class="game-row">
                  <div class="game-row-main">
                    <label class="toggle-switch" :aria-label="`Enable ${game.label}`">
                      <input type="checkbox" v-model="game.enabled" />
                      <span class="toggle-track">
                        <span class="toggle-knob" />
                      </span>
                    </label>
                    <span class="game-label" :class="{ 'game-disabled': !game.enabled }">{{ game.label }}</span>
                    <button
                      type="button"
                      class="btn-text btn-text--sm"
                      :class="{ 'btn-text--active': game.showLimits }"
                      @click="game.showLimits = !game.showLimits"
                    >
                      {{ game.showLimits ? '▾ limits' : '▸ limits' }}
                    </button>
                  </div>
                  <div v-if="game.showLimits" class="game-limits">
                    <label class="inline-label">
                      Min bet ($)
                      <input v-model="game.minBet" type="number" min="0" step="0.01" class="limit-input" placeholder="0.10" />
                    </label>
                    <label class="inline-label">
                      Max bet ($)
                      <input v-model="game.maxBet" type="number" min="0" step="0.01" class="limit-input" placeholder="1000.00" />
                    </label>
                  </div>
                </div>
              </div>

              <p class="field-hint">You can configure bet limits, RTP, and detailed game settings in Settings → Games after setup.</p>
            </div>

            <!-- Step 6: Legal Pages -->
            <div v-if="currentStep === 6" class="step-content">
              <p class="step-intro">
                Your platform includes template legal pages pre-populated with standard content. Review each page in the CMS and replace the placeholder text with your company's actual details before going live.
              </p>

              <div class="legal-pages-list">
                <div v-for="page in legalPageStatuses" :key="page.slug" class="legal-page-row">
                  <div class="legal-status-icon" :class="page.exists ? 'icon-ok' : 'icon-missing'">
                    <svg v-if="page.exists" viewBox="0 0 16 16" fill="none">
                      <path d="M3.5 8l3 3 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else viewBox="0 0 16 16" fill="none">
                      <path d="M4 12L12 4M4 4l8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </div>
                  <div class="legal-page-info">
                    <span class="legal-page-slug">{{ legalPageLabels[page.slug] ?? page.slug }}</span>
                    <span class="legal-page-status" :class="'status-' + page.status">{{ page.exists ? page.status : 'missing' }}</span>
                  </div>
                </div>
              </div>

              <label class="confirm-row">
                <input type="checkbox" v-model="step6Confirmed" class="confirm-checkbox" />
                <span class="confirm-text">
                  I have reviewed the legal pages and understand I must customise them with my company's details before going live.
                </span>
              </label>

              <p class="field-hint">You can edit legal pages anytime in Content → Pages.</p>
            </div>
          </div>

          <!-- ─── Footer ─────────────────────────────────────────────────── -->
          <div class="wizard-footer">
            <div class="footer-left">
              <button
                v-if="currentStep > 1"
                type="button"
                class="btn btn-ghost"
                :disabled="saving"
                @click="goBack"
              >
                ← Back
              </button>
            </div>
            <div class="footer-right">
              <button
                v-if="currentStep < totalSteps"
                type="button"
                class="btn btn-ghost"
                :disabled="saving"
                @click="skipStep"
              >
                Skip
              </button>
              <button
                v-if="currentStep < totalSteps"
                type="button"
                class="btn btn-primary"
                :disabled="saving || !canProceed"
                @click="goNext"
              >
                <span v-if="saving" class="btn-spinner" />
                {{ saving ? 'Saving…' : 'Next →' }}
              </button>
              <button
                v-else
                type="button"
                class="btn btn-primary btn-complete"
                :disabled="saving || !step6Confirmed"
                @click="completeSetup"
              >
                <span v-if="saving" class="btn-spinner" />
                {{ saving ? 'Finishing…' : '🎉 Complete Setup' }}
              </button>
            </div>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import apiClient from '@/api/client'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SetupStatusResponse {
  completed: boolean
  current_step: number
  steps: WizardStepInfo[]
}

interface WizardStepInfo {
  step: number
  title: string
  description: string
  completed: boolean
}

interface ThemePreset {
  id: string
  name: string
  description: string
  thumbnail_colors: string[]
}

interface PaymentMethodItem {
  id: number
  display_name: string
  provider: string
  type: string
  enabled: boolean
}

interface GameEntry {
  slug: string
  label: string
  enabled: boolean
  minBet: string
  maxBet: string
  showLimits: boolean
}

interface LegalPageStatus {
  slug: string
  exists: boolean
  status: string
}

// ─── State ────────────────────────────────────────────────────────────────────

const show = ref(false)
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const currentStep = ref(1)
const steps = ref<WizardStepInfo[]>([])

// Step 1
const step1 = reactive({ siteName: '', timezone: 'UTC', appUrl: '' })

// Step 2
const step2 = reactive({ presetName: '', primaryColor: '#00e701' })
const presets = ref<ThemePreset[]>([])
const presetsLoading = ref(false)
const logoInputEl = ref<HTMLInputElement | null>(null)
const logoFile = ref<File | null>(null)
const logoPreviewUrl = ref('')
const logoUploadError = ref('')

// Step 3
const step3 = reactive({ defaultCurrency: 'USD', currencyDecimalPlaces: 2 })

// Step 4
const step4Methods = ref<PaymentMethodItem[]>([])
const step4Loading = ref(false)

// Step 5
const step5Games = ref<GameEntry[]>([
  { slug: 'dice', label: 'Dice', enabled: true, minBet: '', maxBet: '', showLimits: false },
  { slug: 'mines', label: 'Mines', enabled: true, minBet: '', maxBet: '', showLimits: false },
  { slug: 'crash', label: 'Crash', enabled: true, minBet: '', maxBet: '', showLimits: false },
  { slug: 'plinko', label: 'Plinko', enabled: true, minBet: '', maxBet: '', showLimits: false },
  { slug: 'keno', label: 'Keno', enabled: true, minBet: '', maxBet: '', showLimits: false },
  { slug: 'hilo', label: 'Hi-Lo', enabled: true, minBet: '', maxBet: '', showLimits: false },
  { slug: 'blackjack', label: 'Blackjack', enabled: true, minBet: '', maxBet: '', showLimits: false },
  { slug: 'roulette', label: 'Roulette', enabled: true, minBet: '', maxBet: '', showLimits: false },
  { slug: 'baccarat', label: 'Baccarat', enabled: true, minBet: '', maxBet: '', showLimits: false },
  { slug: 'video-poker', label: 'Video Poker', enabled: true, minBet: '', maxBet: '', showLimits: false },
  { slug: 'slots', label: 'Slots', enabled: true, minBet: '', maxBet: '', showLimits: false },
])

// Step 6
const step6Confirmed = ref(false)
const legalPageStatuses = ref<LegalPageStatus[]>([])

// ─── Constants ────────────────────────────────────────────────────────────────

const commonTimezones = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (ICT)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Seoul', label: 'Asia/Seoul (KST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT/AEST)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZDT/NZST)' },
]

const commonCurrencies = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'PLN', name: 'Polish Zloty' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'BTC', name: 'Bitcoin' },
  { code: 'ETH', name: 'Ethereum' },
  { code: 'USDT', name: 'Tether' },
]

const legalPageLabels: Record<string, string> = {
  'terms': 'Terms of Service',
  'privacy-policy': 'Privacy Policy',
  'responsible-gaming': 'Responsible Gaming',
  'cookie-policy': 'Cookie Policy',
  'aml-policy': 'AML Policy',
  'acceptable-use': 'Acceptable Use Policy',
}

// ─── Computed ─────────────────────────────────────────────────────────────────

const totalSteps = computed(() => steps.value.length || 6)

const activeStep = computed(() =>
  steps.value.find(s => s.step === currentStep.value) ?? null
)

const currencyPreviewValue = computed(() => {
  const decimals = step3.currencyDecimalPlaces
  const sample = 1234.5
  return sample.toFixed(decimals)
})

const canProceed = computed(() => {
  if (currentStep.value === 1) return step1.siteName.trim().length > 0
  if (currentStep.value === 6) return step6Confirmed.value
  return true
})

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(async () => {
  await loadStatus()
})

// ─── Methods ─────────────────────────────────────────────────────────────────

async function loadStatus(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const { data } = await apiClient.get<SetupStatusResponse>('/admin/api/setup/status')
    if (data.completed) {
      show.value = false
      return
    }
    steps.value = data.steps
    currentStep.value = data.current_step
    show.value = true
    await loadStepData(currentStep.value)
  } catch {
    // If we can't load status (e.g. not an admin yet), silently hide the wizard
    show.value = false
  } finally {
    loading.value = false
  }
}

async function loadStepData(step: number): Promise<void> {
  if (step === 2 && presets.value.length === 0) {
    presetsLoading.value = true
    try {
      const { data } = await apiClient.get<ThemePreset[]>('/admin/api/theme/presets')
      presets.value = data
    } catch {
      // presets optional — wizard still works without them
    } finally {
      presetsLoading.value = false
    }
  }

  if (step === 4 && step4Methods.value.length === 0) {
    step4Loading.value = true
    try {
      const { data } = await apiClient.get<{ data: PaymentMethodItem[] }>('/admin/api/payment-methods')
      step4Methods.value = Array.isArray(data) ? data : (data.data ?? [])
    } catch {
      // payment methods optional — wizard still works without them
    } finally {
      step4Loading.value = false
    }
  }
}

function selectPreset(preset: ThemePreset): void {
  step2.presetName = preset.name
  if (preset.thumbnail_colors.length > 0) {
    step2.primaryColor = preset.thumbnail_colors[0]
  }
}

function onLogoChange(event: Event): void {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  const maxMb = 2
  if (file.size > maxMb * 1024 * 1024) {
    logoUploadError.value = `Logo must be under ${maxMb} MB`
    return
  }

  logoFile.value = file
  logoUploadError.value = ''
  logoPreviewUrl.value = URL.createObjectURL(file)
}

function selectAllGames(enabled: boolean): void {
  step5Games.value.forEach(g => { g.enabled = enabled })
}

function goBack(): void {
  if (currentStep.value > 1) {
    currentStep.value--
    error.value = ''
    loadStepData(currentStep.value)
  }
}

function skipStep(): void {
  if (currentStep.value < totalSteps.value) {
    currentStep.value++
    error.value = ''
    loadStepData(currentStep.value)
  }
}

async function goNext(): Promise<void> {
  if (saving.value) return
  saving.value = true
  error.value = ''

  try {
    await saveCurrentStep()

    if (logoFile.value && currentStep.value === 2) {
      await uploadLogo()
    }

    currentStep.value++
    await loadStepData(currentStep.value)
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Failed to save step. Please try again.'
    error.value = msg
  } finally {
    saving.value = false
  }
}

async function saveCurrentStep(): Promise<void> {
  const step = currentStep.value

  if (step === 1) {
    await apiClient.post(`/admin/api/setup/step/1`, {
      site_name: step1.siteName,
      timezone: step1.timezone || undefined,
      app_url: step1.appUrl || undefined,
    })
  } else if (step === 2) {
    await apiClient.post(`/admin/api/setup/step/2`, {
      primary_color: step2.primaryColor || undefined,
      preset_name: step2.presetName || undefined,
    })
  } else if (step === 3) {
    await apiClient.post(`/admin/api/setup/step/3`, {
      default_currency: step3.defaultCurrency,
      currency_decimal_places: step3.currencyDecimalPlaces,
    })
  } else if (step === 4) {
    const payload = step4Methods.value.map(m => ({ id: m.id, enabled: m.enabled }))
    if (payload.length > 0) {
      await apiClient.post(`/admin/api/setup/step/4`, { payment_methods: payload })
    }
  } else if (step === 5) {
    const games = step5Games.value.map(g => {
      const entry: { slug: string; enabled: boolean; min_bet?: string; max_bet?: string } = {
        slug: g.slug,
        enabled: g.enabled,
      }
      if (g.minBet) entry.min_bet = g.minBet
      if (g.maxBet) entry.max_bet = g.maxBet
      return entry
    })
    await apiClient.post(`/admin/api/setup/step/5`, { games })
  } else if (step === 6) {
    const res = await apiClient.post<{ message: string; legal_pages: LegalPageStatus[] }>(
      `/admin/api/setup/step/6`,
      { confirmed: true }
    )
    legalPageStatuses.value = res.data.legal_pages ?? []
  }
}

async function uploadLogo(): Promise<void> {
  if (!logoFile.value) return
  const fd = new FormData()
  fd.append('logo', logoFile.value)
  await apiClient.post('/admin/api/theme/logo', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

async function completeSetup(): Promise<void> {
  if (!step6Confirmed.value || saving.value) return
  saving.value = true
  error.value = ''

  try {
    // Save step 6 first if not saved yet
    await saveCurrentStep()
    await apiClient.post('/admin/api/setup/complete')
    show.value = false
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Failed to complete setup. Please try again.'
    error.value = msg
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
/* ─── Overlay ──────────────────────────────────────────────────────────────── */
.wizard-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.wizard-panel {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.6);
}

/* ─── Fade transition ──────────────────────────────────────────────────────── */
.wizard-fade-enter-active,
.wizard-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.wizard-fade-enter-from,
.wizard-fade-leave-to {
  opacity: 0;
  transform: scale(0.97);
}

/* ─── Header ───────────────────────────────────────────────────────────────── */
.wizard-header {
  padding: 28px 32px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.wizard-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.wizard-brand-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.wizard-brand-text {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.wizard-subtitle {
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
}

/* ─── Step Indicator ───────────────────────────────────────────────────────── */
.wizard-steps-bar {
  display: flex;
  align-items: flex-start;
  padding: 16px 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  overflow-x: auto;
  gap: 0;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 80px;
}

.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid var(--bg-tertiary, #213743);
  background: var(--bg-tertiary, #213743);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
  transition: all 0.2s ease;
  z-index: 1;
}

.step-check {
  width: 14px;
  height: 14px;
  color: #fff;
}

.step-label {
  font-size: 0.7rem;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
  margin-top: 6px;
  line-height: 1.3;
  max-width: 80px;
}

.step-connector {
  position: absolute;
  top: 16px;
  left: calc(50% + 16px);
  right: calc(-50% + 16px);
  height: 2px;
  background: var(--bg-tertiary, #213743);
  transition: background 0.2s ease;
}

.step-item.step-done .step-circle {
  background: var(--accent-primary, #00e701);
  border-color: var(--accent-primary, #00e701);
  color: #000;
}

.step-item.step-done .step-label {
  color: var(--text-primary, #fff);
}

.step-item.step-done .step-connector,
.connector-done {
  background: var(--accent-primary, #00e701);
}

.step-item.step-active .step-circle {
  background: transparent;
  border-color: var(--accent-primary, #00e701);
  color: var(--accent-primary, #00e701);
  box-shadow: 0 0 0 4px rgba(0, 231, 1, 0.1);
}

.step-item.step-active .step-label {
  color: var(--text-primary, #fff);
  font-weight: 600;
}

/* ─── Loading ──────────────────────────────────────────────────────────────── */
.wizard-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 32px;
  color: var(--text-secondary, #b1bad3);
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: var(--accent-primary, #00e701);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-inline {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  padding: 8px 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ─── Body ─────────────────────────────────────────────────────────────────── */
.wizard-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
}

.wizard-body::-webkit-scrollbar {
  width: 4px;
}

.wizard-body::-webkit-scrollbar-thumb {
  background: var(--bg-tertiary, #213743);
  border-radius: 4px;
}

.step-header {
  margin-bottom: 24px;
}

.step-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.step-desc {
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
}

.step-intro {
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.6;
  margin: 0 0 20px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  border-left: 3px solid var(--accent-primary, #00e701);
}

.wizard-error {
  padding: 10px 14px;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: 8px;
  color: #fca5a5;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

/* ─── Form fields ──────────────────────────────────────────────────────────── */
.step-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.required {
  color: var(--accent-primary, #00e701);
}

.field-input,
.field-select {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary, #fff);
  font-size: 0.9rem;
  padding: 10px 14px;
  width: 100%;
  outline: none;
  transition: border-color 0.15s;
}

.field-input:focus,
.field-select:focus {
  border-color: var(--accent-primary, #00e701);
}

.field-input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.field-hint {
  font-size: 0.8rem;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.field-error {
  font-size: 0.8rem;
  color: #fca5a5;
  margin: 0;
}

/* ─── Theme presets ────────────────────────────────────────────────────────── */
.presets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 8px;
}

.preset-card {
  background: var(--bg-tertiary, #213743);
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
  transition: border-color 0.15s, transform 0.15s;
}

.preset-card:hover {
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.preset-card.preset-active {
  border-color: var(--accent-primary, #00e701);
  background: rgba(0, 231, 1, 0.05);
}

.preset-swatches {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.preset-swatch {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: inline-block;
}

.preset-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.preset-desc {
  font-size: 0.7rem;
  color: var(--text-tertiary, #7f8fa4);
}

/* ─── Color input ──────────────────────────────────────────────────────────── */
.color-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.color-swatch-input {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 8px;
  padding: 2px;
  background: var(--bg-tertiary, #213743);
  cursor: pointer;
  flex-shrink: 0;
}

.color-hex-input {
  flex: 1;
  max-width: 180px;
}

/* ─── Logo upload ──────────────────────────────────────────────────────────── */
.logo-upload-area {
  background: var(--bg-tertiary, #213743);
  border: 2px dashed rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 24px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: border-color 0.15s;
  position: relative;
}

.logo-upload-area:hover {
  border-color: rgba(255, 255, 255, 0.3);
}

.logo-preview {
  max-height: 60px;
  max-width: 200px;
  object-fit: contain;
}

.logo-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 0.875rem;
}

.upload-icon {
  width: 32px;
  height: 32px;
  color: var(--text-tertiary, #7f8fa4);
}

.upload-hint {
  font-size: 0.75rem;
  color: var(--text-tertiary, #7f8fa4);
}

.hidden-file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

/* ─── Currency preview ─────────────────────────────────────────────────────── */
.currency-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.preview-label {
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
}

.preview-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}

/* ─── Payment methods ──────────────────────────────────────────────────────── */
.methods-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.method-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  gap: 12px;
}

.method-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.method-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.method-meta {
  font-size: 0.75rem;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: capitalize;
}

.empty-notice {
  color: var(--text-secondary, #b1bad3);
  font-size: 0.875rem;
  padding: 12px;
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
}

/* ─── Toggle switch ────────────────────────────────────────────────────────── */
.toggle-switch {
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-switch input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-track {
  width: 40px;
  height: 22px;
  background: var(--bg-primary, #0f1923);
  border-radius: 11px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  position: relative;
  transition: background 0.2s;
}

.toggle-switch input:checked + .toggle-track {
  background: var(--accent-primary, #00e701);
  border-color: var(--accent-primary, #00e701);
}

.toggle-knob {
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  top: 2px;
  left: 2px;
  transition: transform 0.2s;
}

.toggle-switch input:checked + .toggle-track .toggle-knob {
  transform: translateX(18px);
}

/* ─── Games ────────────────────────────────────────────────────────────────── */
.games-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.btn-text {
  background: none;
  border: none;
  color: var(--accent-primary, #00e701);
  font-size: 0.8rem;
  cursor: pointer;
  padding: 4px 0;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.btn-text:hover {
  opacity: 0.8;
}

.btn-text--sm {
  font-size: 0.75rem;
}

.btn-text--active {
  opacity: 0.8;
}

.games-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.game-row {
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
}

.game-row-main {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
}

.game-label {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary, #fff);
  transition: color 0.15s;
}

.game-disabled {
  color: var(--text-tertiary, #7f8fa4);
}

.game-limits {
  display: flex;
  gap: 12px;
  padding: 8px 14px 10px 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.1);
}

.inline-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: var(--text-secondary, #b1bad3);
}

.limit-input {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--text-primary, #fff);
  font-size: 0.8rem;
  padding: 4px 8px;
  width: 90px;
  outline: none;
}

.limit-input:focus {
  border-color: var(--accent-primary, #00e701);
}

/* ─── Legal pages ──────────────────────────────────────────────────────────── */
.legal-pages-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.legal-page-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.legal-status-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.legal-status-icon svg {
  width: 12px;
  height: 12px;
}

.icon-ok {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
}

.icon-missing {
  background: rgba(239, 68, 68, 0.15);
  color: #fca5a5;
}

.legal-page-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.legal-page-slug {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary, #fff);
}

.legal-page-status {
  font-size: 0.75rem;
  text-transform: capitalize;
}

.status-published {
  color: var(--accent-primary, #00e701);
}

.status-draft {
  color: #fbbf24;
}

.status-missing {
  color: #fca5a5;
}

.confirm-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  padding: 12px 14px;
  background: rgba(0, 231, 1, 0.05);
  border: 1px solid rgba(0, 231, 1, 0.2);
  border-radius: 8px;
}

.confirm-checkbox {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: var(--accent-primary, #00e701);
}

.confirm-text {
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.5;
}

/* ─── Footer ───────────────────────────────────────────────────────────────── */
.wizard-footer {
  padding: 16px 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: var(--bg-secondary, #1a2c38);
}

.footer-left,
.footer-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s, transform 0.1s;
  outline: none;
}

.btn:active { transform: scale(0.98); }

.btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.btn-ghost:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-primary, #fff);
}

.btn-complete {
  padding: 10px 28px;
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.3);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* ─── Responsive ───────────────────────────────────────────────────────────── */
@media (max-width: 640px) {
  .wizard-panel {
    max-height: 95vh;
    border-radius: 12px;
  }

  .wizard-header {
    padding: 20px 20px 16px;
  }

  .wizard-steps-bar {
    padding: 12px 20px;
  }

  .wizard-body {
    padding: 16px 20px;
  }

  .wizard-footer {
    padding: 12px 20px;
  }

  .step-label {
    display: none;
  }

  .presets-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .game-limits {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
